import React, { useState, useCallback } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Trash2, Loader2, GripVertical } from 'lucide-react';
import { ItemSearchModal } from '@/app/pages/sales-orders/components/ItemSearchModal';
import { itemCollectionFormSchema, ItemCollectionFormValues } from './schema';
import { ItemCollection } from '@/app/types/itemCollection';
import { toast } from 'sonner';

interface CollectionFormProps {
  initialData?: ItemCollection | null;
  onSubmit: (values: ItemCollectionFormValues) => void;
  isSubmitting: boolean;
  disabled?: boolean;
}

export const CollectionForm: React.FC<CollectionFormProps> = ({
  initialData,
  onSubmit,
  isSubmitting,
  disabled = false,
}) => {
  const [isItemSearchOpen, setIsItemSearchOpen] = useState(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(null);

  const form = useForm<ItemCollectionFormValues>({
    resolver: zodResolver(itemCollectionFormSchema),
    defaultValues: {
      name: initialData?.name || '',
      slug: initialData?.slug || '',
      description: initialData?.description || '',
      is_active: initialData?.is_active ?? true,
      items: initialData?.items?.map((item, index) => ({
        id: item.id,
        item_id: item.id,
        name: item.name,
        code: item.code,
        sort_order: item.sort_order || index,
      })) || [],
    },
  });

  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: 'items',
  });

  const handleAddItem = () => {
    setSelectedItemIndex(fields.length);
    setIsItemSearchOpen(true);
  };

  const handleSelectItem = (item: any) => {
    const newItem = {
      id: item.id,
      item_id: item.id,
      name: item.name,
      code: item.code || item.sku,
      sort_order: fields.length,
    };

    if (selectedItemIndex !== null && selectedItemIndex < fields.length) {
      // Update existing item
      form.setValue(`items.${selectedItemIndex}`, newItem);
    } else {
      // Add new item
      append(newItem);
    }

    setIsItemSearchOpen(false);
    setSelectedItemIndex(null);
  };

  const handleRemoveItem = (index: number) => {
    remove(index);
    // Update sort_order for remaining items
    const items = form.getValues('items');
    items.forEach((_, idx) => {
      form.setValue(`items.${idx}.sort_order`, idx);
    });
  };

  const handleMoveItem = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < fields.length) {
      move(index, newIndex);
      // Update sort_order after moving
      const items = form.getValues('items');
      items.forEach((_, idx) => {
        form.setValue(`items.${idx}.sort_order`, idx);
      });
    }
  };

  // Generate slug from name
  const handleNameChange = (value: string) => {
    form.setValue('name', value);
    if (!initialData) {
      // Only auto-generate slug for new collections
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      form.setValue('slug', slug);
    }
  };

  const handleSubmit = (values: ItemCollectionFormValues) => {
    // Validate at least one item if needed
    if (values.items.length === 0) {
      toast.warning('Consider adding items to your collection');
    }
    onSubmit(values);
  };

  const handleFormError = (errors: any) => {
    console.error('Form validation errors:', errors);
    toast.error('Please check the form for errors');
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit, handleFormError)} className="space-y-6">
        {/* Collection Details */}
        <Card>
          <CardHeader>
            <CardTitle>Collection Details</CardTitle>
            <CardDescription>Basic information about your item collection</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Bestsellers, New Arrivals"
                      disabled={disabled}
                      {...field}
                      onChange={(e) => handleNameChange(e.target.value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Slug *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., bestsellers, new-arrivals"
                      disabled={disabled}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>URL-friendly identifier</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe this collection..."
                      disabled={disabled}
                      rows={3}
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Active Status</FormLabel>
                    <FormDescription>
                      Make this collection visible to customers
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={disabled}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Items Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Collection Items</CardTitle>
                <CardDescription>Products in this collection</CardDescription>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddItem}
                disabled={disabled}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {fields.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Item Name</TableHead>
                    <TableHead>Code/SKU</TableHead>
                    <TableHead>Sort Order</TableHead>
                    <TableHead className="w-20">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fields.map((field, index) => (
                    <TableRow key={field.id}>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleMoveItem(index, 'up')}
                            disabled={disabled || index === 0}
                          >
                            ↑
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleMoveItem(index, 'down')}
                            disabled={disabled || index === fields.length - 1}
                          >
                            ↓
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{field.name}</div>
                      </TableCell>
                      <TableCell>
                        {field.code || '-'}
                      </TableCell>
                      <TableCell>
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveItem(index)}
                          disabled={disabled}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                No items added yet. Click "Add Item" to start building your collection.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => window.history.back()}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={disabled || isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              initialData ? 'Update Collection' : 'Create Collection'
            )}
          </Button>
        </div>
      </form>

      {/* Item Search Modal */}
      <ItemSearchModal
        isOpen={isItemSearchOpen}
        onOpenChange={setIsItemSearchOpen}
        onSelectItem={handleSelectItem}
      />
    </Form>
  );
};